/* eslint-disable @typescript-eslint/no-explicit-any */
import {isObject, snakeCase} from 'lodash';
import {getRange} from 'utils/array';
import {isNil, toSnakeCase, withValues} from 'utils/object';

export const cleanFormData = (
    formData?: FormData | Record<string, unknown>,
) => {
    if (!formData) return undefined;

    if (import.meta.env.VITEST === 'true') {
        return JSON.stringify(
            toSnakeCase(
                withValues(
                    formData instanceof FormData
                        ? Object.fromEntries(formData.entries())
                        : formData,
                    true,
                ),
            ),
        );
    }

    if (formData instanceof FormData) {
        const clean = new FormData();

        for (const pair of formData.entries()) {
            const [key, value] = pair;

            if (!isNil(value)) {
                clean.append(snakeCase(key), value);
            }
        }

        return clean;
    }

    return JSON.stringify(toSnakeCase(withValues(formData, true)));
};

const toFormData = (formData: Record<string, any>) => {
    const data = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
        if (isObject(value)) {
            if (value instanceof FileList) {
                // add all files from FileList
                const range = getRange(0, value.length - 1);
                range.forEach((index) => {
                    data.append(snakeCase(key), value[index]);
                });
            } else {
                // DropdownSelectRecord
                Object.keys(value).forEach((item) => {
                    data.append(snakeCase(key), item);
                });
            }
        } else {
            data.append(snakeCase(key), value);
        }
    });

    return data;
};

// For testing inside of Jest
const toParams = (formData: FormData | Record<string, unknown>) => {
    const params = new URLSearchParams();
    const data =
        formData instanceof FormData
            ? Object.fromEntries(formData.entries())
            : formData;
    Object.entries(data).forEach(([key, value]: [string, any]) => {
        if (value instanceof FileList) {
            const range = getRange(0, value.length - 1);
            const files: string[] = [];
            range.forEach((index) => {
                files.push(value[index].name);
            });
            params.append(key, files.join(','));
        } else if (typeof value === 'string') {
            params.append(key, value);
        } else if (Array.isArray(value)) {
            params.append(key, value.join(','));
        } else {
            // DropdownSelectRecord
            params.append(key, Object.keys(value).join(','));
        }
    });

    return params.toString();
};

export const encodeFormData = (
    formData?: FormData | Record<string, unknown>,
) => {
    if (!formData) return undefined;

    if (import.meta.env.VITEST === 'true') {
        return toParams(formData);
    }

    if (formData instanceof FormData) {
        const snakeCased = new FormData();

        for (const pair of formData.entries()) {
            const [key, value] = pair;

            snakeCased.append(snakeCase(key), value);
        }

        return snakeCased;
    }

    return toFormData(formData);
};
